import { Box, Button, Card, CardContent, CircularProgress, Grid, List, ListItem, ListItemText, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDTypography from 'components/MDTypography'
import dayjs from 'dayjs'
import Footer from 'examples/Footer'
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import { supabase } from 'pages/supabaseClient'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify';
import ReactToPrint from 'react-to-print';
import QRCode from 'qrcode';
import axios from 'axios'
import DataNotFound from 'components/NoData/dataNotFound'
import noTicketData from "assets/images/illustrations/noTicketData.png";
import TopBar from 'TailwindcssComponents/TopBar'
import TailwindLayout from 'TailwindcssComponents/TailwindLayout'
import FooterTailwind from 'TailwindcssComponents/Footer'

export default function SingleTicketView() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const { ticketId, eventId } = useParams();
    const [ticketData, setTicketData] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [qrCodes, setQrCodes] = useState({});
    const [registrationFormData, setRegistrationFormData] = useState([]);
    const componentRefs = useRef([]);

    const getTicketData = async () => {
        try {
            // Fetch the single ticket data first
            const ticketResponse = await axios.get(`${apiUrl}event-service/event-ticket/${ticketId}`);
            if (!ticketResponse.data?.success || !ticketResponse.data?.data) {
                throw new Error('Failed to fetch event ticket');
            }

            const ticket = ticketResponse?.data.data; // Single ticket object
            const { userId, eventId, registrationId, ticketCategoryId } = ticket || {};

            // Fetch related data in parallel using their IDs.
            if (userId && eventId && registrationId && ticketCategoryId) {


                const [userResponse, eventResponse, registrantResponse, paymentResponse, categoryResponse] = await Promise.all([
                    axios.get(`${apiUrl}auth-service/auth/${userId}`, { headers: { 'Authorization': `Bearer ${bearerToken}` } }),
                    axios.get(`${apiUrl}event-service/event/${eventId}`),
                    axios.get(`${apiUrl}event-service/event-registration/${registrationId}`),
                    axios.get(`${apiUrl}event-service/event-payment`, { params: { registration: registrationId } }),
                    axios.get(`${apiUrl}event-service/ticket-category/${ticketCategoryId}`, { headers: { 'Authorization': `Bearer ${bearerToken}` } })
                ]);

                // Validate responses
                if (!userResponse.data?.success || !userResponse.data?.data) {
                    throw new Error('Failed to fetch user details');
                }
                if (!eventResponse.data?.success || !eventResponse.data?.data) {
                    throw new Error('Failed to fetch event details');
                }
                if (!registrantResponse.data?.success || !registrantResponse.data?.data) {
                    throw new Error('Failed to fetch registrant details');
                }
                if (!paymentResponse.data?.success || !paymentResponse.data?.data) {
                    throw new Error('Failed to fetch payment details');
                }
                if (!categoryResponse.data?.success || !categoryResponse.data?.data) {
                    throw new Error('Failed to fetch ticket category');
                }

                // Extract event details
                const event = eventResponse.data.data;
                const venueId = event.venueId;

                // Fetch venue and event category details by their IDs
                const [venueResponse] = await Promise.all([
                    axios.get(`${apiUrl}event-service/event-venue/${venueId}`),
                ]);

                // Validate responses
                if (!venueResponse.data?.success || !venueResponse.data?.data) {
                    throw new Error('Failed to fetch venue details');
                }

                // Prepare the enriched ticket data
                const enrichedTicket = {
                    ...ticket,
                    user: userResponse.data.data,
                    event: {
                        ...event,
                        venue: venueResponse.data.data,
                    },
                    registrant: registrantResponse.data.data,
                    payment: paymentResponse.data.data,
                    category: categoryResponse.data.data,
                    qrCode: await generateQRCode(ticket.referenceId, eventId, ticket.id) // Generate QR code
                };

                // Update the state with the enriched ticket data
                setTicketData(enrichedTicket);
            }
        } catch (error) {
            console.error('Error in fetching ticket data:', error.message);
        }
    };

    const fetchRegistrationFormField = async () => {
        try {
            const response = await axios.get(`${apiUrl}event-service/event-registration`);
            if (response.data?.success && response.data?.data) {
                setRegistrationFormData(response.data?.data);
            } else {
                throw new Error('Failed to fetch form fields data');
            }
        } catch (error) {
            console.log(error.response?.data?.message || error.message);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await getTicketData();
                await fetchRegistrationFormField();
                // await fetchEventData();
                // await fetchOrganization();
            } catch (error) {
                console.error('Error fetching data', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [ticketId]);

    const allKeysOfRegisteredEvents = [
        ...new Set(
            Object.keys(ticketData.eventRegistrations?.details || {})
        )
    ];
    const allKeysOfForm = [...new Set(
        registrationFormData
            .flatMap(item => Object.keys(item.details))
    )];

    const cancelTicket = async (id) => {
        try {
            const { data, error } = await supabase
                .from('tickets_events')
                .update({ isActive: false })
                .eq('id', id)
                .select('*');
            if (error) {
                throw error;
            }
            if (data) {
                fetchEventData();
                toast.warning('Ticket has been cancelled!');
                console.log('Ticket Cancelled successfully');
            }
        } catch (error) {
            console.error('Error in ticket cancelling:', error.message);
        }
    };

    const formatDateTime = (date) => {
        const d = new Date(date);

        // Format date as DD/MM/YYYY
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
        const year = d.getFullYear();

        // Format time as HH:MM (24-hour format)
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    const generateQRCode = async (referenceId, eventId, ticketId) => {
        try {
            const qrCodeDataUrl = await QRCode.toDataURL(String(`ReferenceID:${referenceId} && EventID:${eventId} && TicketID:${ticketId}`));
            setQrCodes(qrCodeDataUrl);
        } catch (error) {
            console.error("Error generating QR code", error);
        }
    };

    return (
        <TailwindLayout>
            <TopBar />
            <div className='p-6 mb-12'>
            {isLoading ? (
                <MDBox p={3} display="flex" justifyContent="center">
                    <CircularProgress color="info" />
                </MDBox>
            ) :
                <>
                    {ticketData && Object.keys(ticketData).length > 0 ? (
                        <MDBox sx={{ position: "relative", mt: 2, p: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={5} lg={5}>
                                    <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '20px' }}>
                                        <Card style={{ marginLeft: '10px', width: '450px' }}>
                                            <CardContent>
                                                {ticketData.registrant && (
                                                    <div style={{ marginBottom: '15px' }}>
                                                        {ticketData.registrant.details ? (
                                                            <List>
                                                                <ListItem>
                                                                    <ListItemText
                                                                        primary={<strong>Name:</strong>}
                                                                        secondary={ticketData.registrant?.name || '--'}
                                                                    />
                                                                </ListItem>
                                                                {Object.entries(ticketData.registrant.details).map(([key, value]) => (
                                                                    <ListItem key={key}>
                                                                        <ListItemText
                                                                            primary={<strong>{key}:</strong>}
                                                                            secondary={value || '--'}
                                                                        />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        ) : (
                                                            <Typography>No details available</Typography>
                                                        )}
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </div>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4} lg={5}>
                                    <div ref={componentRefs}>
                                        <Box sx={{ flexGrow: 1, mt: 3, mb: 2 }}>
                                            <Grid container spacing={3} justifyContent="center">
                                                <Grid item xs={12}>
                                                    <Card sx={{
                                                        position: 'relative',
                                                        p: 2,
                                                        border: '2px solid',
                                                        borderRadius: 0,
                                                        boxSizing: 'border-box',
                                                        width: '100%',
                                                    }}>
                                                        <Box sx={{
                                                            backgroundColor: '#e0e0e0',
                                                            textAlign: 'center',
                                                            mt: 1,
                                                            mb: 3,
                                                            padding: '10px'
                                                        }}>
                                                            <MDTypography
                                                                variant="h2"
                                                                sx={{
                                                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                                                    mb: 2,
                                                                    backgroundColor: '#e0e0e0',
                                                                    display: 'inline-block',
                                                                    width: 'auto',
                                                                }}
                                                            >
                                                                {ticketData.event?.organization?.name}
                                                            </MDTypography>

                                                            <Box sx={{
                                                                textAlign: 'right',
                                                                mb: 1
                                                            }}>
                                                                <MDTypography
                                                                    variant='body2'
                                                                    sx={{ fontSize: { xs: '0.75rem', md: '1rem' } }}
                                                                >
                                                                    Reference ID: {ticketData.referenceId}
                                                                </MDTypography>
                                                            </Box>

                                                            <img
                                                                src={ticketData.event?.coverImage?.secure_url}
                                                                alt="Event"
                                                                style={{
                                                                    width: '100%',
                                                                    display: 'block',
                                                                    marginTop: '10px',
                                                                    borderRadius: '4px',
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box display="flex" alignItems='center' mt={3}>
                                                            <MDTypography sx={{ mr: 1 }}>Event:</MDTypography>
                                                            <MDTypography variant='h5' sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }}>{ticketData.event?.name}</MDTypography>
                                                        </Box>
                                                        <Box display="flex" alignItems='center' mt={1}>
                                                            <MDTypography sx={{ mr: 1 }}>Time:</MDTypography>
                                                            <MDTypography variant='h5' sx={{ fontSize: { xs: '1rem', md: '1.3rem' } }}>
                                                                {formatDateTime(ticketData.event?.start)}
                                                            </MDTypography>
                                                        </Box>
                                                        <Box display="flex" alignItems='center' mt={1}>
                                                            <MDTypography sx={{ mr: 1 }}>Venue:</MDTypography>
                                                            <MDTypography variant='h5' sx={{ fontSize: { xs: '1rem', md: '1.3rem' } }}>{(ticketData.event?.venue?.name)}</MDTypography>
                                                        </Box>
                                                        <Box display="flex" alignItems='center' mt={1}>
                                                            <MDTypography sx={{ mr: 1 }}>Category:</MDTypography>
                                                            <MDTypography variant='h5' sx={{ fontSize: { xs: '1rem', md: '1.3rem' } }}>{(ticketData?.category?.name)}</MDTypography>
                                                        </Box>
                                                        <Box display="flex" alignItems='center' mt={1}>
                                                            <MDTypography sx={{ mr: 1 }}>Price:</MDTypography>
                                                            <MDTypography variant='h5' sx={{ fontSize: { xs: '1rem', md: '1.3rem' } }}>{(ticketData.price)}</MDTypography>
                                                        </Box>
                                                        <Box display="flex" alignItems='center' mt={1}>
                                                            {ticketData?.seats_events != null &&
                                                                <>
                                                                    <MDTypography sx={{ mr: 1 }}>Seat:</MDTypography>
                                                                    <MDTypography variant='h5' sx={{ fontSize: { xs: '1rem', md: '1.3rem' } }}> {ticketData[0]?.seats_events?.seatName}</MDTypography>
                                                                </>
                                                            }
                                                        </Box>
                                                        <Grid container mt={3}>
                                                            <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: { xs: 'center', sm: 'flex-start' } }}>
                                                                <MDTypography sx={{ fontSize: { xs: '0.75rem', md: '1rem' } }}>{(ticketData.created_at)}</MDTypography>
                                                                <MDTypography sx={{ fontSize: { xs: '0.75rem', md: '1rem' } }}>Seatsnaps</MDTypography>
                                                            </Grid>
                                                            <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: { xs: 'center', sm: 'flex-end' } }}>
                                                                {qrCodes && (
                                                                    <img src={qrCodes} alt="qr" style={{ height: '200px', width: '200px', border: '1px solid', maxWidth: '100%' }} />
                                                                )}
                                                            </Grid>
                                                        </Grid>
                                                    </Card>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </div>
                                    <Box mt={3} sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                        {/* <MDButton color='warning' variant={ticketData?.isDeleted ? 'contained' : 'text'} onClick={() => cancelTicket(ticketData[0].id)} disabled={!ticketData[0]?.isActive}>
                                            {ticketData?.isDeleted ? 'Cancel Ticket' : 'Cancelled'}
                                        </MDButton> */}
                                        <ReactToPrint
                                            trigger={() => (
                                                <MDButton
                                                    color='primary'
                                                    variant='contained'
                                                // disabled={!ticketData?.isDeleted}
                                                >
                                                    Print Ticket
                                                </MDButton>
                                            )}
                                            content={() => componentRefs.current}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>


                        </MDBox>
                    )
                        :
                        (<DataNotFound message={'Ticket Data Not Found '} image={noTicketData} />)}
                </>

            }
            </div>
            <FooterTailwind />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </TailwindLayout>
    )
}
